/* ============================================================================
   AUTH GATE — wraps the console.
   - authMode 'demo'     : render the app as-is (prototype identity picker).
   - authMode 'firebase' : require a real Yperea (QA) session first. On first
     logon (no session) show the phone-auth screen; once authenticated, render
     the console and a small "Connected to QA" badge.

   Cloudflare Access still sits in front of the whole site — this gate adds the
   per-user backend authentication on top. See documentation/plan/07.
   ============================================================================ */
import React from 'react';
import { CONFIG } from '../config';
import { getSession, logout, type SessionInfo } from './api';
import { loadLive } from '../live/liveStore';
import { hydrateLiveStore } from '../live/liveDataSource';
import { PhoneAuthScreen } from './PhoneAuthScreen';

const CONSOLE_LS = 'yperea_console_v1';

/** Sign out of QA: clear the console state, drop the BFF session + Firebase, reload
 *  (→ phone auth). Exposed so the design's in-app "Log out" uses it in live mode
 *  instead of the mock persona login (which doesn't exist in the hydrated store). */
async function qaSignOut(): Promise<void> {
  try {
    localStorage.removeItem(CONSOLE_LS);
  } catch {
    /* ignore */
  }
  await logout();
  try {
    const fb = await import('../firebase/auth');
    await fb.firebaseSignOut();
  } catch {
    /* ignore */
  }
  location.reload();
}
(window as unknown as { __qaSignOut: () => void }).__qaSignOut = qaSignOut;

function Splash(props: { text: string }): React.ReactElement {
  return (
    <div
      className="yp-root"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        color: 'var(--sub)',
        fontSize: 13,
      }}
    >
      {props.text}
    </div>
  );
}

function QaBadge(props: { user?: SessionInfo['user'] }): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const signOut = qaSignOut;
  const name = props.user?.username || props.user?.full_name || 'authenticated';
  return (
    <div style={{ position: 'fixed', bottom: 14, right: 16, zIndex: 300 }}>
      <span
        className="yp-btn"
        onClick={() => setOpen((o) => !o)}
        title="Authenticated against the Yperea QA backend"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 11,
          color: 'var(--green)',
          background: 'var(--green-soft)',
          border: '1px solid var(--green)',
          borderRadius: 999,
          padding: '5px 11px',
          backdropFilter: 'blur(8px)',
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)' }} />
        QA · {name}
      </span>
      {open ? (
        <div
          style={{
            position: 'absolute',
            bottom: '120%',
            right: 0,
            background: 'var(--panel-2)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            padding: 10,
            minWidth: 200,
            boxShadow: 'var(--shadow)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--sub)', padding: '2px 6px 8px' }}>
            Live session on the Yperea <b style={{ color: 'var(--ink)' }}>{CONFIG.env.toUpperCase()}</b> backend
            {props.user?.id ? (
              <span style={{ display: 'block', color: 'var(--faint)', fontSize: 10, marginTop: 2 }}>
                uid {props.user.id}
              </span>
            ) : null}
          </div>
          <div
            className="yp-nav-item"
            onClick={signOut}
            style={{ padding: '8px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'var(--red)' }}
          >
            Sign out of QA
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AuthGate(props: { children: React.ReactNode }): React.ReactElement {
  // Demo mode: no real backend auth — the prototype identity picker handles login.
  if (CONFIG.authMode !== 'firebase') {
    return <>{props.children}</>;
  }

  const [phase, setPhase] = React.useState<'connecting' | 'needauth' | 'hydrating' | 'ready'>('connecting');
  const [session, setSession] = React.useState<SessionInfo>({ authenticated: false });

  // On a real session: swap the console's data plane to live (hydrate the store
  // from QA) BEFORE rendering, so the original design screens render live data.
  const begin = React.useCallback((s: SessionInfo) => {
    setSession(s);
    const user = s.user;
    if (!s.authenticated || !user) {
      setPhase('needauth');
      return;
    }
    setPhase('hydrating');
    loadLive(true)
      .then((bundle) =>
        hydrateLiveStore(bundle, {
          id: user.id,
          username: user.username,
          full_name: user.full_name ?? null,
          email: user.email ?? null,
        }),
      )
      .catch((e: unknown) => console.warn('[Yperea] live hydration failed; showing mock store:', e))
      .finally(() => setPhase('ready'));
  }, []);

  React.useEffect(() => {
    let alive = true;
    getSession().then((s) => {
      if (alive) begin(s);
    });
    return () => {
      alive = false;
    };
  }, [begin]);

  if (phase === 'connecting') return <Splash text="Connecting to Yperea…" />;
  if (phase === 'needauth') return <PhoneAuthScreen onAuthed={begin} />;
  if (phase === 'hydrating') return <Splash text="Loading live data from QA…" />;

  return (
    <>
      {props.children}
      <QaBadge user={session.user} />
    </>
  );
}
